import React, { useEffect, useState } from "react";
import { callApi } from "../../api/callApi";
import { API_ENDPOINT, API_URL_2 } from "../../api/constant";
import { IModifierCollection, IModifierGroup, IProductModifierGroup } from "../../interfaces/modifier";

interface ModifierGroupsConfigProps {
    value: IProductModifierGroup[];
    onChange: (value: IProductModifierGroup[]) => void;
}

/**
 * Lets an admin attach one or more Modifier Groups to a Product / Combo Product and
 * configure each one.
 * Filtering by Modifier Collection is supported to easily locate groups.
 */
const ModifierGroupsConfig: React.FC<ModifierGroupsConfigProps> = ({ value, onChange }) => {
    const [availableGroups, setAvailableGroups] = useState<IModifierGroup[]>([]);
    const [collections, setCollections] = useState<IModifierCollection[]>([]);
    const [selectedCollectionId, setSelectedCollectionId] = useState<string>("");
    const [selectedToAdd, setSelectedToAdd] = useState<string>("");

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [groupsRes, collectionsRes] = await Promise.all([
                callApi(`${API_URL_2}${API_ENDPOINT.page}modifier?all=true`, { method: "GET" }),
                callApi(`${API_URL_2}${API_ENDPOINT.page}modifier/main?all=true`, { method: "GET" }),
            ]);

            const groupList: IModifierGroup[] = Array.isArray(groupsRes) ? groupsRes : groupsRes?.data || [];
            const collectionList: IModifierCollection[] = Array.isArray(collectionsRes) ? collectionsRes : collectionsRes?.data || [];

            setAvailableGroups(groupList);
            setCollections(collectionList);
        } catch (error: any) {
            console.error("Failed to fetch modifier groups or collections:", error.message);
        }
    };

    const addedIds = new Set(value.map((v) => v.modifierGroupId));

    // Filter available groups based on selected Modifier Collection
    const filteredGroups = availableGroups.filter((group) => {
        if (!selectedCollectionId) return true; // Show all if no collection selected

        const collection = collections.find((c) => c.id === selectedCollectionId);
        
        // Check if group belongs to selected collection (either from collection side or group side)
        const isGroupInCollection =
            (collection?.modifierGroupIds && collection.modifierGroupIds.includes(group.id)) ||
            (collection?.modifierGroups && collection.modifierGroups.some((g) => g.id === group.id)) ||
            (group.modifierIds && group.modifierIds.includes(selectedCollectionId)) ||
            (group.modifierId === selectedCollectionId) ||
            (group.modifiers && group.modifiers.some((m) => m.id === selectedCollectionId));

        return isGroupInCollection;
    });

    const selectableGroups = filteredGroups.filter((g) => !addedIds.has(g.id));

    const handleAdd = () => {
        if (!selectedToAdd) return;
        const group = availableGroups.find((g) => g.id === selectedToAdd);
        if (!group) return;
        const newEntry: IProductModifierGroup = {
            modifierGroupId: group.id,
            name: group.name,
            isRequired: false,
            minSelection: 0,
            maxSelection: 1,
            selectionType: "SINGLE",
            maxQtyPerOption: 1,
        };
        onChange([...value, newEntry]);
        setSelectedToAdd("");
    };

    const handleRemove = (modifierGroupId: string) => {
        onChange(value.filter((v) => v.modifierGroupId !== modifierGroupId));
    };

    const handleUpdate = (
        modifierGroupId: string,
        field: keyof IProductModifierGroup,
        fieldValue: string | number | boolean
    ) => {
        onChange(
            value.map((v) => (v.modifierGroupId === modifierGroupId ? { ...v, [field]: fieldValue } : v))
        );
    };

    const groupName = (entry: IProductModifierGroup) =>
        entry.name || availableGroups.find((g) => g.id === entry.modifierGroupId)?.name || entry.modifierGroupId;

    return (
        <div>
            {/* Step 1: Filter by Modifier Collection & Step 2: Select Modifier Group */}
            <div className="row g-3 align-items-end mb-4">
                <div className="col-12 col-md-5">
                    <label className="form-label fw-bold">Modifier Collection (Filter)</label>
                    <select
                        className="form-control"
                        value={selectedCollectionId}
                        onChange={(e) => {
                            setSelectedCollectionId(e.target.value);
                            setSelectedToAdd("");
                        }}
                    >
                        <option value="">— All Collections —</option>
                        {collections.map((c) => (
                            <option key={c.id} value={c.id}>
                                {c.label}
                            </option>
                        ))}
                    </select>
                    <small className="text-muted d-block mt-1">Select a collection to filter modifier groups.</small>
                </div>

                <div className="col-12 col-md-5">
                    <label className="form-label fw-bold">Modifier Group</label>
                    <select
                        className="form-control"
                        value={selectedToAdd}
                        onChange={(e) => setSelectedToAdd(e.target.value)}
                    >
                        <option value="">Select a modifier group</option>
                        {selectableGroups.map((g) => (
                            <option key={g.id} value={g.id}>
                                {g.name}
                            </option>
                        ))}
                    </select>
                    {selectedCollectionId && selectableGroups.length === 0 && (
                        <small className="text-muted d-block mt-1">No remaining groups in this collection.</small>
                    )}
                </div>

                <div className="col-12 col-md-2 d-grid d-md-block">
                    <button
                        type="button"
                        className="btn app-btn-primary text-nowrap w-100"
                        onClick={handleAdd}
                        disabled={!selectedToAdd}
                    >
                        <i className="bi bi-plus-lg me-1" /> Add Group
                    </button>
                </div>
            </div>

            {/* Selected Groups List */}
            {value.length === 0 ? (
                <p className="text-muted">No modifier groups attached.</p>
            ) : (
                value.map((entry) => (
                    <div key={entry.modifierGroupId} className="border rounded p-3 mb-3 bg-light">
                        <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                            <h6 className="fw-bold mb-0 text-break">{groupName(entry)}</h6>
                            <button
                                type="button"
                                className="btn btn-danger px-2 py-1 text-white flex-shrink-0"
                                onClick={() => handleRemove(entry.modifierGroupId)}
                            >
                                <i className="bi bi-trash3" />
                            </button>
                        </div>
                        <div className="row">
                            <div className="mb-3 col-md-3 col-6">
                                <label className="form-label">Min Selection</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    min={0}
                                    value={entry.minSelection}
                                    onChange={(e) =>
                                        handleUpdate(entry.modifierGroupId, "minSelection", Number(e.target.value))
                                    }
                                />
                            </div>
                            <div className="mb-3 col-md-3 col-6">
                                <label className="form-label">Max Selection</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    min={0}
                                    value={entry.maxSelection}
                                    onChange={(e) =>
                                        handleUpdate(entry.modifierGroupId, "maxSelection", Number(e.target.value))
                                    }
                                />
                            </div>
                            <div className="mb-3 col-md-3 col-6">
                                <label className="form-label">Max Qty Per Option</label>
                                <input
                                    type="number"
                                    className="form-control"
                                    min={1}
                                    value={entry.maxQtyPerOption}
                                    onChange={(e) =>
                                        handleUpdate(entry.modifierGroupId, "maxQtyPerOption", Number(e.target.value))
                                    }
                                />
                            </div>
                            <div className="mb-3 col-md-3 col-6 d-flex align-items-center">
                                <div className="form-check mt-3">
                                    <input
                                        className="form-check-input"
                                        type="checkbox"
                                        id={`required-${entry.modifierGroupId}`}
                                        checked={entry.isRequired}
                                        onChange={(e) =>
                                            handleUpdate(entry.modifierGroupId, "isRequired", e.target.checked)
                                        }
                                    />
                                    <label className="form-check-label" htmlFor={`required-${entry.modifierGroupId}`}>
                                        Required
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>
                ))
            )}
        </div>
    );
};

export default ModifierGroupsConfig;
